import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Clipboard, File, RotateCcw, SquareArrowOutUpRight } from "lucide-react";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { RecycleBinFilesViewType, RecycleBinJobDocumentsViewType } from "@/lib/supabase/supabaseTypes";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { deDE } from "@mui/x-data-grid/locales";
import { useRecycleBinStore } from "../../store/RecycleBin";
import { showNotification } from "../../store/Central/selectors";
import { useNavigate } from "react-router-dom";
import { JobDocumentTypeEnum } from "../job-page/job-document/job-document.types";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { pageToIcon } from "../../layout/layout-components/app-bar/app-bar-components/app-bar-lists/page-to-icon";
import { cn } from "@/dentconnect/src/lib/util";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { FeatureGateOverlay } from "../../components/subscription/feature-gate-overlay.component";

const JobDocumentTypeToLabel = {
  [JobDocumentTypeEnum.Quotation]: "Kostenvoranschlag",
  [JobDocumentTypeEnum.DeliveryNote]: "Lieferschein",
  [JobDocumentTypeEnum.CreditNote]: "Gutschrift",
  [JobDocumentTypeEnum.Tracing]: "Rückverfolgung",

}

export const RecycleBinPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<number>(0);
  const { organization } = useCentralStore((state) => ({
    organization: state.organization,
  }));
  const navigate = useNavigate();
  const { clientsLookup } = useCentralStore((state) => ({
    clientsLookup: state.clientsLookup,
  }));


  const { recycleBinFiles, fetchRecycleBinFiles, restoreFile, fetchRecycleBinDocuments, recycleBinJobDocuments, restoreJobDocument } = useRecycleBinStore((state) => ({
    recycleBinFiles: state.recycleBinFiles,
    fetchRecycleBinFiles: state.fetchRecycleBinFiles,
    restoreFile: state.restoreFile,
    fetchRecycleBinDocuments: state.fetchRecycleBinDocuments,
    recycleBinJobDocuments: state.recycleBinJobDocuments,
    restoreJobDocument: state.restoreJobDocument,
  }));

  const handleRestoreFile = async (file: RecycleBinFilesViewType) => {
    if (await restoreFile(file)) {
      showNotification({
        message: "Datei erfolgreich wiederhergestellt",
        action: {
          label: "Zum Auftrag wechseln",
          onClick: () => {
            navigate(`/client/${file.client_id}/job/${file.job_id}`);
          },
        },
        type: "success",
      });
    }
  }

  const handleRestoreJobDocument = async (jobDocument: RecycleBinJobDocumentsViewType) => {
    if (await restoreJobDocument(jobDocument)) {
      showNotification({
        message: "Job-Dokument erfolgreich wiederhergestellt",
        action: {
          label: "Zum Auftrag wechseln",
          onClick: () => {
            navigate(`/client/${jobDocument.client_id}/job/${jobDocument.job_id}`);
          },
        },
        type: "success",
      });
    }
  }

  useEffect(() => {
    if (organization?.id) {
      fetchRecycleBinDocuments();
      fetchRecycleBinFiles(organization.id);
    }
  }, [organization, fetchRecycleBinFiles, fetchRecycleBinDocuments]);

  useEffect(() => {
    if (organization?.id) {
      if (selectedOption === 0) {
        fetchRecycleBinFiles(organization.id);
        fetchRecycleBinDocuments(organization.id);
      }
    }
  }, [selectedOption, fetchRecycleBinFiles, fetchRecycleBinDocuments]);

  useEffect(() => {
    if (!recycleBinFiles) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [recycleBinFiles]);

  const fileColumns: GridColDef[] = [
    {
      field: 'file_name',
      headerName: 'Dateiname',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'job_code',
      headerName: 'Auftragscode',
      flex: 1,
      width: 130,
      valueGetter: (params) => params.row.job_code || '-',
    },
    {
      field: 'job_title',
      headerName: 'Auftragstitel',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.job_title || '-',
    },
    {
      field: 'created_at',
      headerName: 'Erstellt am',
      flex: 1,
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value as string).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
    },
    {
      field: 'deleted_at',
      headerName: 'Gelöscht am',
      flex: 1,
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value as string).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="flex items-center justify-center">
          <Tooltip title="Wiederherstellen">
            <button
              onClick={() => handleRestoreFile(params.row)}
              className="p-1 rounded-full hover:bg-blue-50 text-blue-600"
              title="Wiederherstellen"
            >
              <RotateCcw size={18} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const jobDocumentColumns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Titel des Dokuments',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'type',
      headerName: 'Typ des Dokuments',
      flex: 1,
      width: 130,
      valueGetter: (params) => JobDocumentTypeToLabel[params.row.type as JobDocumentTypeEnum] || '-',
    },
    {
      field: 'date',
      headerName: 'Datum des Dokuments',
      flex: 1,
      width: 130,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value as string).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
    },
    {
      field: 'job_code',
      headerName: 'Auftragscode',
      flex: 1,
      width: 130,
      valueGetter: (params) => params.row.job_code || '-',
    },
    {
      field: 'job_title',
      headerName: 'Auftragstitel',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.job_title || '-',
    },
    {
      field: 'client_id',
      headerName: 'Auftraggeber',
      flex: 1,
      width: 130,
      valueGetter: (params) => clientsLookup[params.row.client_id] ? getClientDisplayName(clientsLookup[params.row.client_id]) : '-',
    },
    {
      field: 'deleted_at',
      headerName: 'Gelöscht am',
      flex: 1,
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value as string).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="flex items-center justify-center">
          <Tooltip title="Wiederherstellen">
            <button
              onClick={() => handleRestoreJobDocument(params.row)}
              className="p-1 rounded-full hover:bg-blue-50 text-blue-600"
            >
              <RotateCcw size={18} />
            </button>
          </Tooltip>
          <Tooltip title="Zum Auftrag wechseln">
            <button
              onClick={() => navigate(`/client/${params.row.client_id}/job/${params.row.job_id}`)}
              className="p-1 rounded-full hover:bg-blue-50 text-blue-600"
            >
              <SquareArrowOutUpRight size={18} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <FeatureGateOverlay>
    <div className="p-6 h-full w-full">
      <HeaderPortal>
      <div className="flex flex-row items-center gap-4">
        <PageTitle title={[{ name: "Papierkorb" }]} icon={pageToIcon("recycle-bin", {
          width: "16px",
          height: "16px",
          color: "rgb(55, 65, 81)",
        })} />
        <div className="flex items-center gap-1">
          <button
            onClick={() => setSelectedOption(0)}
            className={cn(
              "flex items-center gap-1 px-3 py-1 text-sm rounded-md transition-colors bg hover:bg-stone-200",
              selectedOption === 0 ? "bg-stone-200 text-gray-800 font-medium" : "text-gray-700"
            )}
          >
            <File size={16} />
            <span>Dateien</span>
          </button>
          <button
            onClick={() => setSelectedOption(1)}
            className={cn(
              "flex items-center gap-1 px-3 py-1 text-sm rounded-md transition-colors bg hover:bg-stone-200",
              selectedOption === 1 ? "bg-stone-200 text-gray-800 font-medium" : "text-gray-700"
            )}
          >
            <Clipboard size={16} />
            <span>Dokumente</span>
          </button>
          </div>
        </div>
      </HeaderPortal>
      <div className="h-[calc(100vh-12rem)] w-full">
        {loading ? (
          <div className="flex justify-center items-center h-full w-full">
            <CircularProgress />
          </div>
        ) : (
          selectedOption === 0 ? (
            <DataGrid
              rows={recycleBinFiles}
              columns={fileColumns}
              loading={loading}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              disableRowSelectionOnClick
              autoHeight
              density="standard"
              initialState={{
                sorting: {
                  sortModel: [{ field: 'deleted_at', sort: 'desc' }],
                },
                filter: {
                  filterModel: {
                    items: [],
                  },
                },
              }}
            />
          ) : (
            <DataGrid
              rows={recycleBinJobDocuments}
              columns={jobDocumentColumns}
              loading={loading}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              disableRowSelectionOnClick
              autoHeight
              density="standard"
              initialState={{
                sorting: {
                  sortModel: [{ field: 'deleted_at', sort: 'desc' }],
                },
                filter: {
                  filterModel: {
                    items: [],
                  },
                },
              }}
            />
          )
        )}
      </div>
    </div>
    </FeatureGateOverlay>
  );
};
